import { useRef, useEffect, useMemo, useState } from 'react';
import { initEcharts, applyChartTheme } from 'lib/echarts';
import {
  RobotContributionMsgReplyRateDay,
  RobotContributionMsgReplyReplyDetailDay,
} from 'services/api/dashboard';
import dayjs from 'lib/dayjs';
import { useTranslate } from '@tolgee/react';
import { millisecondDurationDisplay } from 'views/Dashboard/ImplementData/utils';
import { message } from 'antd';
import { formatNumber } from 'utils/numberFormat';

interface RateChartProps {
  replyRateDays: RobotContributionMsgReplyRateDay[];
  replyDetailDays: RobotContributionMsgReplyReplyDetailDay[];
}

const maxSelectedLength = 4;
let selectedLegendIndex: boolean[] = [true, true, true, true, false, false, false];

export const RateChart = (props: RateChartProps) => {
  const { replyRateDays, replyDetailDays } = props;
  const chartRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslate();

  const [selectLegendLength, setSelectLegendLength] = useState(
    selectedLegendIndex.filter(Boolean).length,
  );

  const averageReplyTime = useMemo(() => {
    if (replyRateDays.length === 0) return '00:00:00';

    const sum = replyRateDays.reduce((acc, item) => acc + item.replyTime, 0);
    const result = sum / replyRateDays.length;

    return result ? millisecondDurationDisplay(result) : '00:00:00';
  }, [replyRateDays]);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = initEcharts(chartRef.current);

    // 模拟数据 - 实际使用时应该从 API 获取
    const dates = replyRateDays.map((item) => dayjs(item.dt).format('MM-DD'));
    const percent = (num: number) => (num * 100).toFixed(2);

    const data = {
      replyRate: replyRateDays.map((item) => percent(item.replyRate)),
      humanReplyRate: replyRateDays.map((item) => percent(item.humanReplyRate)),
      aiReplyRate: replyRateDays.map((item) => percent(item.aiReplyRate)),
      autoReplyRate: replyRateDays.map((item) => percent(item.autoReplyRate)),
      queryCount: replyDetailDays.map((item) => item.queryCount),
      replyCount: replyDetailDays.map((item) => item.replyCount),
      aiReplyCount: replyDetailDays.map((item) => item.aiReplyCount),
      humanReplyCount: replyDetailDays.map((item) => item.humanReplyCount),
    };

    const legend = [
      t('买家消息数'),
      t('回复率'),
      t('24小时回复率-智能机器人'),
      t('24小时回复率-人工客服'),
      t('回复消息数'),
      t('回复消息数-智能机器人'),
      t('回复消息数-人工客服'),
    ];

    const option = {
      tooltip: {
        trigger: 'axis',
        formatter: function (params: any[]) {
          // 只显示未被隐藏的系列数据
          let result = params[0].name + '<br/>';
          params.forEach((param) => {
            if (param.componentSubType === 'line' && param.data) {
              result +=
                param.seriesName +
                ': ' +
                formatNumber(Number(param.value) / 100, '0.00%') +
                '<br/>';
            }
            if (param.componentSubType === 'bar' && param.data) {
              result += param.seriesName + ': ' + param.value + '<br/>';
            }
          });
          return result;
        },
      },
      legend: {
        data: legend,
        top: 0,
        left: 'center',
        selected: legend.reduce((pre, cur, index) => {
          return {
            ...pre,
            [cur]: selectedLegendIndex[index],
          };
        }, {}),
      },
      grid: {
        left: '6%',
        right: '6%',
        containLabel: true,
      },
      dataZoom: [
        {
          realtime: true,
          start: 0,
          end: 100,
        },
        {
          type: 'inside',
          realtime: true,
          start: 0,
          end: 100,
        },
      ],
      xAxis: {
        type: 'category',
        boundaryGap: true,
        data: dates,
        axisLabel: {
          formatter: (value: string) => value.slice(5), // 只显示月-日
        },
      },
      yAxis: [
        {
          type: 'value',
          name: '',
        },
        {
          type: 'value',
          name: '%',
        },
      ],
      series: [
        {
          name: legend[0],
          type: 'bar',
          data: data.queryCount,
        },
        {
          name: legend[1],
          type: 'line',
          data: data.replyRate,
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          yAxisIndex: 1,
        },
        {
          name: legend[2],
          type: 'line',
          data: data.aiReplyRate,
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          yAxisIndex: 1,
        },
        {
          name: legend[3],
          type: 'line',
          data: data.humanReplyRate,
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          yAxisIndex: 1,
        },
        {
          name: legend[4],
          type: 'bar',
          data: data.replyCount,
        },
        {
          name: legend[5],
          type: 'bar',
          data: data.aiReplyCount,
        },
        {
          name: legend[6],
          type: 'bar',
          data: data.humanReplyCount,
        },
      ],
    };

    chart.setOption(applyChartTheme(option));

    const onSelectedChange = (params: any) => {
      const data = params as { name: string; selected: Record<string, boolean> };
      const { name, selected } = data;
      const len = Object.values(selected).filter(Boolean).length;
      selectedLegendIndex = Object.values(selected);
      if (len > 4) {
        chart.dispatchAction({
          type: 'legendUnSelect',
          name,
        });
        message.warn(t('ViewDashboard.您同时只可以选择x个数据。', { x: maxSelectedLength }));
      } else {
        setSelectLegendLength(len);
      }
    };
    chart.on('legendselectchanged', onSelectedChange);
    // 响应式处理
    const handleResize = () => {
      chart.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.off('legendselectchanged', onSelectedChange);
      chart.dispose();
    };
  }, [replyDetailDays, replyRateDays, t]);

  return (
    <div className="w-full">
      <div className="flex items-center gap-4 text-[var(--fett-text-secondary)] mb-4 justify-between">
        <div>
          {t('平均响应时长')}:{averageReplyTime}
        </div>
        <div>
          {t('ViewDashboard.数据已选择x', { count: selectLegendLength, max: maxSelectedLength })}
        </div>
      </div>
      <div ref={chartRef} className="w-full h-[340px]"></div>
    </div>
  );
};
